import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import type { IDockviewPanelProps } from 'dockview-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api, type Connector, type InvokeOutcome, type ToolSpec } from '../lib/api';
import { errorMessage } from '../lib/format';
import { useConnectors } from './market';

/**
 * MCP Apps tile (SEP-1865, host side). A connector tool that carries `_meta.ui.resourceUri` renders its
 * own `ui://` HTML view: we read it through the API, load it in the sandbox proxy iframe (different
 * origin when TAH_SANDBOX_ORIGIN is set; otherwise an opaque sandbox) and speak the JSON-RPC postMessage
 * protocol with it — initialize, tool-input/tool-result, app-initiated tools/call (through the same
 * Execute path and D6 policy as the chat; order-placing tools ask the user first), resources/read,
 * open-link. Everything the view sends is untrusted.
 */
export interface McpAppParams {
  connector?: string;
  tool: string;
  args: Record<string, unknown>;
}

const PROTOCOL = '2026-01-26';

export const uiResourceUri = (t: ToolSpec): string | undefined => {
  const meta = t._meta as
    | { ui?: { resourceUri?: unknown }; 'ui/resourceUri'?: unknown }
    | undefined;
  const v = meta?.ui?.resourceUri ?? meta?.['ui/resourceUri'];
  return typeof v === 'string' && v.startsWith('ui://') ? v : undefined;
};

interface RpcMessage {
  jsonrpc: '2.0';
  id?: number | string;
  method?: string;
  params?: unknown;
  result?: unknown;
  error?: { code: number; message: string };
}

/** Where the sandbox proxy page is served from: a second host of the same API, never the SPA's origin. */
export function useSandboxOrigin() {
  const cfg = useQuery({
    queryKey: ['config'],
    queryFn: api.config,
    staleTime: Number.POSITIVE_INFINITY,
  });
  return useMemo(() => {
    const here = window.location;
    if (cfg.data?.sandboxOrigin) return { origin: cfg.data.sandboxOrigin, isolated: true };
    if (here.hostname === 'localhost')
      return {
        origin: `${here.protocol}//127.0.0.1${here.port ? `:${here.port}` : ''}`,
        isolated: true,
      };
    if (here.hostname === '127.0.0.1')
      return {
        origin: `${here.protocol}//localhost${here.port ? `:${here.port}` : ''}`,
        isolated: true,
      };
    return { origin: here.origin, isolated: false };
  }, [cfg.data]);
}

/** Converts our normalized Execute outcome back into the CallToolResult shape the view expects. */
function toCallToolResult(o: InvokeOutcome): Record<string, unknown> {
  if (o.status === 'ok' || o.status === 'tool_error') {
    const r = o.result;
    return {
      content: [...(r.text ? [{ type: 'text', text: r.text }] : []), ...(r.other ?? [])],
      ...(r.structured !== undefined ? { structuredContent: r.structured } : {}),
      isError: r.isError,
    };
  }
  return {
    content: [{ type: 'text', text: o.status === 'denied' ? `denied: ${o.reason}` : o.error }],
    isError: true,
  };
}

const theme = () =>
  window.matchMedia('(prefers-color-scheme: light)').matches
    ? ('light' as const)
    : ('dark' as const);
const cssVar = (n: string) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();

export function McpAppTile({ api: panel, params }: IDockviewPanelProps<McpAppParams>) {
  const connectors = useConnectors();
  const connectorName =
    params.connector ?? connectors.data?.connectors.find((c) => c.status === 'active')?.name;
  const detail = useQuery({
    queryKey: ['connector', connectorName],
    queryFn: () => api.connector(connectorName as string),
    enabled: Boolean(connectorName),
    staleTime: 60_000,
  });
  const uiTools = useMemo(
    () => (detail.data?.tools ?? []).filter((t) => uiResourceUri(t)),
    [detail.data],
  );
  const tool = detail.data?.tools.find((t) => t.name === params.tool) ?? uiTools[0];
  const resourceUri = tool ? uiResourceUri(tool) : undefined;
  const resource = useQuery({
    queryKey: ['ui-resource', connectorName, resourceUri],
    queryFn: () => api.resource(connectorName as string, resourceUri as string),
    enabled: Boolean(connectorName && resourceUri),
    staleTime: 10 * 60_000,
  });
  const sandbox = useSandboxOrigin();
  const frame = useRef<HTMLIFrameElement>(null);
  const box = useRef<HTMLDivElement>(null);
  const [phase, setPhase] = useState<'loading' | 'proxy' | 'view' | 'ready' | 'error'>('loading');
  const [error, setError] = useState<string | null>(null);
  const [argsText, setArgsText] = useState(JSON.stringify(params.args ?? {}));
  const [argsOpen, setArgsOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    panel.setTitle(tool ? `${connectorName ?? ''} · ${tool.name}` : 'App');
  }, [panel, tool, connectorName]);

  const html = resource.data?.contents.find((c) => typeof c.text === 'string')?.text;
  const resMeta = resource.data?.contents[0]?._meta as
    | { ui?: { csp?: unknown; permissions?: unknown; prefersBorder?: boolean } }
    | undefined;

  const post = useCallback(
    (msg: RpcMessage) => {
      frame.current?.contentWindow?.postMessage(msg, sandbox.isolated ? sandbox.origin : '*');
    },
    [sandbox],
  );

  const runTool = useCallback(
    async (name: string, args: Record<string, unknown>) => {
      if (!connectorName) throw new Error('no connector');
      return api.invoke(connectorName, name, args);
    },
    [connectorName],
  );

  const dims = () => {
    const r = box.current?.getBoundingClientRect();
    return {
      width: Math.max(1, Math.round(r?.width ?? 400)),
      height: Math.max(1, Math.round(r?.height ?? 300)),
    };
  };

  // Host side of the protocol. One listener per tile; messages are matched by the iframe's window.
  // biome-ignore lint/correctness/useExhaustiveDependencies: refreshKey re-runs the tool on purpose
  useEffect(() => {
    if (!html || !tool) return;
    initialized.current = false;
    setPhase('proxy');
    setError(null);
    const connector: Connector | undefined = detail.data?.connector;
    const orderPlacing = new Set(connector?.capabilityMap.orderPlacing ?? []);
    const onMessage = async (ev: MessageEvent) => {
      if (ev.source !== frame.current?.contentWindow) return;
      if (sandbox.isolated && ev.origin !== sandbox.origin) return;
      const msg = ev.data as RpcMessage;
      if (!msg || msg.jsonrpc !== '2.0') return;
      const reply = (id: number | string, result: unknown) => post({ jsonrpc: '2.0', id, result });
      const fail = (id: number | string, message: string, code = -32000) =>
        post({ jsonrpc: '2.0', id, error: { code, message } });
      switch (msg.method) {
        case 'ui/notifications/sandbox-proxy-ready':
          setPhase('view');
          post({
            jsonrpc: '2.0',
            method: 'ui/notifications/sandbox-resource-ready',
            params: {
              html,
              ...(resMeta?.ui?.csp ? { csp: resMeta.ui.csp } : {}),
              ...(resMeta?.ui?.permissions ? { permissions: resMeta.ui.permissions } : {}),
            },
          });
          return;
        case 'ui/initialize':
          if (msg.id === undefined) return;
          reply(msg.id, {
            protocolVersion: PROTOCOL,
            hostInfo: { name: 'tah-workstation', version: '0.1.0' },
            hostCapabilities: { serverTools: {}, serverResources: {}, openLinks: {}, logging: {} },
            hostContext: {
              toolInfo: {
                tool: {
                  name: tool.name,
                  description: tool.description ?? '',
                  inputSchema: tool.inputSchema,
                },
              },
              theme: theme(),
              styles: {
                variables: {
                  '--color-background-primary': cssVar('--bg'),
                  '--color-background-secondary': cssVar('--panel'),
                  '--color-background-tertiary': cssVar('--panel-2'),
                  '--color-text-primary': cssVar('--text'),
                  '--color-text-secondary': cssVar('--muted'),
                  '--color-border-primary': cssVar('--border'),
                  '--color-text-danger': cssVar('--danger'),
                  '--color-text-success': cssVar('--ok'),
                  '--font-mono': cssVar('--mono'),
                },
              },
              displayMode: 'inline',
              availableDisplayModes: ['inline'],
              containerDimensions: dims(),
              locale: navigator.language,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              userAgent: 'tah-workstation',
              platform: 'web',
              deviceCapabilities: { touch: navigator.maxTouchPoints > 0, hover: true },
            },
          });
          return;
        case 'ui/notifications/initialized': {
          initialized.current = true;
          setPhase('ready');
          const args = params.args ?? {};
          post({
            jsonrpc: '2.0',
            method: 'ui/notifications/tool-input',
            params: { arguments: args },
          });
          try {
            const outcome = await runTool(tool.name, args);
            post({
              jsonrpc: '2.0',
              method: 'ui/notifications/tool-result',
              params: toCallToolResult(outcome),
            });
            if (outcome.status === 'failed') setError(outcome.error);
          } catch (e) {
            setError(errorMessage(e));
            post({
              jsonrpc: '2.0',
              method: 'ui/notifications/tool-cancelled',
              params: { reason: errorMessage(e) },
            });
          }
          return;
        }
        case 'ping':
          if (msg.id !== undefined) reply(msg.id, {});
          return;
        case 'tools/call': {
          if (msg.id === undefined) return;
          const p = (msg.params ?? {}) as { name?: unknown; arguments?: unknown };
          const name = typeof p.name === 'string' ? p.name : '';
          const args = (
            p.arguments && typeof p.arguments === 'object' ? p.arguments : {}
          ) as Record<string, unknown>;
          if (!name) return fail(msg.id, 'invalid tool name', -32602);
          if (
            orderPlacing.has(name) &&
            !window.confirm(
              `The ${connectorName} app wants to call ${name} with ${JSON.stringify(args).slice(0, 300)}. Allow?`,
            )
          )
            return fail(msg.id, 'denied by user');
          try {
            const outcome = await runTool(name, args);
            if (outcome.status === 'denied') return fail(msg.id, `denied: ${outcome.reason}`);
            if (outcome.status === 'failed') return fail(msg.id, outcome.error);
            return reply(msg.id, toCallToolResult(outcome));
          } catch (e) {
            return fail(msg.id, errorMessage(e));
          }
        }
        case 'resources/read': {
          if (msg.id === undefined) return;
          const uri = (msg.params as { uri?: unknown } | undefined)?.uri;
          if (typeof uri !== 'string' || !connectorName) return fail(msg.id, 'invalid uri', -32602);
          try {
            return reply(msg.id, await api.resource(connectorName, uri));
          } catch (e) {
            return fail(msg.id, errorMessage(e));
          }
        }
        case 'ui/open-link': {
          if (msg.id === undefined) return;
          const url = (msg.params as { url?: unknown } | undefined)?.url;
          if (typeof url !== 'string' || !/^https?:\/\//.test(url))
            return fail(msg.id, 'Invalid URL');
          window.open(url, '_blank', 'noopener,noreferrer');
          return reply(msg.id, {});
        }
        case 'ui/request-display-mode':
          if (msg.id !== undefined) reply(msg.id, { mode: 'inline' });
          return;
        case 'ui/message':
        case 'ui/update-model-context':
          if (msg.id !== undefined)
            fail(msg.id, `${msg.method} is not supported by this host yet`, -32601);
          return;
        case 'notifications/message':
        case 'ui/notifications/size-changed':
          return; // fixed-size tile; logs are not surfaced
        default:
          if (msg.id !== undefined && msg.method)
            fail(msg.id, `unknown method ${msg.method}`, -32601);
      }
    };
    window.addEventListener('message', onMessage);
    const ro = new ResizeObserver(() => {
      if (initialized.current)
        post({
          jsonrpc: '2.0',
          method: 'ui/notifications/host-context-changed',
          params: { containerDimensions: dims() },
        });
    });
    if (box.current) ro.observe(box.current);
    return () => {
      window.removeEventListener('message', onMessage);
      ro.disconnect();
      if (initialized.current)
        post({
          jsonrpc: '2.0',
          id: 'teardown',
          method: 'ui/resource-teardown',
          params: { reason: 'tile closed or reloaded' },
        });
    };
  }, [
    html,
    tool,
    detail.data,
    params.args,
    post,
    runTool,
    sandbox,
    connectorName,
    refreshKey,
    resMeta,
  ]);

  if (!connectorName && !connectors.isLoading)
    return (
      <div className="tile pad">
        <span className="muted">
          No active connector. <Link to="/connectors">Add one</Link> to host its apps here.
        </span>
      </div>
    );
  if (detail.data && uiTools.length === 0)
    return (
      <div className="tile pad">
        <span className="muted">Connector {connectorName} publishes no MCP App views.</span>
      </div>
    );

  const sandboxAttr = sandbox.isolated
    ? 'allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox'
    : 'allow-scripts allow-forms allow-popups';
  const status = resource.error
    ? errorMessage(resource.error)
    : error
      ? error
      : phase === 'loading' || resource.isLoading
        ? 'loading view…'
        : phase === 'proxy'
          ? 'starting sandbox…'
          : phase === 'view'
            ? 'loading app…'
            : 'live';
  return (
    <div className="tile mcp-app-tile">
      <div className="tile-bar">
        <select
          aria-label="App tool"
          value={tool?.name ?? ''}
          onChange={(e) => panel.updateParameters({ ...params, tool: e.target.value })}
          style={{ width: 'auto', maxWidth: 220 }}
        >
          {uiTools.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name}
            </option>
          ))}
        </select>
        <button type="button" className="small" onClick={() => setArgsOpen((o) => !o)}>
          args
        </button>
        <button type="button" className="small" onClick={() => setRefreshKey((k) => k + 1)}>
          Refresh
        </button>
        <span className="grow" />
        <span
          className={`muted${error || resource.error ? ' error' : ''}`}
          data-testid="app-status"
        >
          {status}
        </span>
      </div>
      {argsOpen && (
        <form
          className="tile-bar"
          onSubmit={(e) => {
            e.preventDefault();
            try {
              const next = JSON.parse(argsText) as Record<string, unknown>;
              panel.updateParameters({ ...params, args: next });
              setArgsOpen(false);
            } catch (err) {
              setError(`args: ${errorMessage(err)}`);
            }
          }}
        >
          <input
            aria-label="Tool arguments (JSON)"
            value={argsText}
            onChange={(e) => setArgsText(e.target.value)}
            className="mono"
          />
          <button type="submit" className="small primary">
            Apply
          </button>
        </form>
      )}
      <div
        ref={box}
        className="mcp-app-box"
        style={resMeta?.ui?.prefersBorder ? { border: '1px solid var(--border)' } : undefined}
      >
        {html && (
          <iframe
            key={`${connectorName}-${resourceUri}-${refreshKey}`}
            ref={frame}
            title={tool ? `${connectorName} ${tool.name}` : 'MCP app'}
            src={`${sandbox.origin}/mcp-apps/sandbox.html`}
            sandbox={sandboxAttr}
            className="mcp-app-frame"
          />
        )}
      </div>
    </div>
  );
}
